import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {FilterDropdownBox} from '../../../../../components/common/filters/filterDropdownBox/filterDropdownBox';
import {getMetricLabels, maybeRenderFeaturePillAndTooltip} from '../../../../helpers/metricLabels';
import {ChartSerie} from '../../../charts/commons/types';
import {ChartLegendBadge} from '../../../charts/components/legend/chartLegendBadge';
import {AnyGraphMetricsEnum} from '../../helperTypes';

/*
 * Props.
 */

interface MetricsSelectorProps<T> {
  availableMetrics: ReadonlyArray<AnyGraphMetricsEnum>;
  selectedMetric: AnyGraphMetricsEnum | undefined;
  handleSelectedMetric: (arg: AnyGraphMetricsEnum) => void;
  chartSeries: Array<ChartSerie<T>>;
  selectedLabel?: ReactNode;
}

/*
 * Styling.
 */

const StyledMetricsSelectionWrapperDiv = styled.div`
  ${flexCenter};
  margin-right: 8px;
  margin-top: 6px;
`;

const StyledSimpleDropdownItem = styled(SimpleDropdownItem)`
  > div:first-child {
    ${flexCenter};
    gap: 5px;
  }
`;

/*
 * Component.
 */

export const GraphMetricsSelectionDropdown = <T,>(props: MetricsSelectorProps<T>): ReturnType<FC> => {
  const {availableMetrics, selectedMetric, handleSelectedMetric, chartSeries, selectedLabel} = props;

  const renderDropdown = (modalProps: PopoverTransferProps) => (
    <SimpleDropdown {...modalProps}>
      {availableMetrics.map((metric) => {
        const {label, isBeta, isNew, tooltip} = getMetricLabels(metric);
        return (
          <StyledSimpleDropdownItem
            key={metric}
            isChecked={selectedMetric === metric}
            onSelect={() => handleSelectedMetric(metric)}
          >
            {label}
            {maybeRenderFeaturePillAndTooltip(isBeta, isNew, tooltip)}
          </StyledSimpleDropdownItem>
        );
      })}
    </SimpleDropdown>
  );

  return (
    <StyledMetricsSelectionWrapperDiv>
      <ChartLegendBadge color={chartSeries[0].color} />
      <FilterDropdownBox renderButton={() => selectedLabel} renderDropdown={renderDropdown} />
    </StyledMetricsSelectionWrapperDiv>
  );
};
